import React, {useCallback, useMemo, useRef} from 'react';
import styled from 'styled-components';
import AgGridWrapper from '../../../BaseComponentWrappers/AgGridWrapper';
import {currentResource, labels} from '../../../../../util/utils';

export default function MultiEntityListTable(props) {
	const {
		columnDefs,
		rowData,
		frameworkComponents,
		optionsPerPage,
		page,
		pageSize,
		pageCount,
		onPageSizeChanged,
		onPageChanged
	} = props;

	const defaultColDef = useMemo(() => {
		return {
			minWidth: 100,
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true
		};
	}, []);

	const gridRef = useRef();
	const handleOnGridReady = useCallback((params) => {
		gridRef.current = params.api;
		const grids = document.querySelectorAll(`section.AGGridWrapperSection`);
		if (grids && grids.length > 0) {
			grids.forEach((grid) => {
				grid.querySelector('.ag-body-viewport').classList.add('customScrollbar');
			});
			resizeGrid(params);

			window.addEventListener('resize', function() {
				resizeGrid(params);
			});
		}
	}, []);

	const resizeGrid = (params) => {
		setTimeout(function() {
			params.api.sizeColumnsToFit();
		}, 200);
	};

	const gridStyle = useMemo(() => ({height: '100%', width: '100%'}), []);

	return (
		<StyledMultiEntityListTable className="StyledMultiEntityListTable">
			<section style={gridStyle} className="AgGridContainerStyles">
				<section style={gridStyle} className="AGGridWrapperSection">
					<AgGridWrapper
						ref={gridRef}
						defaultColDef={defaultColDef}
						rowData={rowData}
						columnDefs={columnDefs}
						suppressRowClickSelection
						suppressCellFocus
						suppressContextMenu
						domLayout="normal"
						components={frameworkComponents}
						autoHeaderHeight
						onReady={handleOnGridReady}
						customPagination
						optionsPerPage={optionsPerPage}
						page={page}
						pageSize={pageSize}
						pageCount={pageCount}
						onPageSizeChanged={onPageSizeChanged}
						onPageChanged={onPageChanged}
						paginationButtonLabels={currentResource.pagingSvgHoverText}
						paginationSelectLabel={labels.pagingShowtext}
						overlayNoRowsTemplate={labels.noEntitiesFound}
					/>
				</section>
			</section>
		</StyledMultiEntityListTable>
	);
}

const StyledMultiEntityListTable = styled.section`
	height: 100%;
	.AGGridWrapperSection {
		.ag-ltr {
			.ag-row-group-indent-0,
			.ag-row-group-indent-1,
			.ag-row-group-indent-2 {
				align-items: center;
				.ag-group-expanded,
				.ag-group-contracted {
					min-height: var(--px-22);
					height: auto;
					margin-right: var(--px-5);
					.ag-icon {
						&::before {
							fill: var(--neutrals-900);
							color: var(--neutrals-900);
						}
					}
				}
			}
			.ag-row-group-indent-1 {
				padding-left: var(--px-20);
			}
			.ag-row-group-indent-2 {
				align-items: center;
				padding-left: var(--px-45);
			}
			.ag-row-group-leaf-indent {
				margin-left: var(--px-20);
				&.ag-row-group-indent-2 {
					padding-left: var(--px-48);
				}
				&.ag-row-group-indent-3 {
					padding-left: var(--px-66);
					.StyledNameColumn {
						font-size: var(--px-12) !important;
					}
				}
			}
		}
		.ag-header {
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
						height: var(--px-56) !important;
						.ag-header-group-cell {
							font-size: var(--px-14);
							font-weight: 400;
							padding: 0 0 0 var(--px-10);
							justify-content: flex-end;
							&[col-id='7_0'] {
								justify-content: center;
								.StyledIconColumnHeader {
									justify-content: center;
								}
							}
						}
						.ag-header-cell {
							.ag-header-cell-text {
								font-size: var(--px-14);
								font-weight: 400;
							}
							&[col-id='stEntityShortName'] {
								padding-left: var(--px-16);
								.ag-header-cell-text,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-start !important;
									text-align: left;
									color: var(--neutrals-900);
								}
							}
							&[col-id='stEntityName'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-start;
									text-align: left;
									color: var(--neutrals-900);
								}
							}
							&[col-id='entityGroupName'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-start;
									text-align: left;
									color: var(--neutrals-900);
								}
							}
							&[col-id='actions'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: center;
									text-align: center;
									color: var(--neutrals-900);
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			.ag-row {
				&.ag-row-level-0 {
					.ag-cell {
						&[col-id='stEntityShortName'],
						&[col-id='entityGroupName'] {
							.StyledStringColumn {
								font-size: var(--px-14);
								font-weight: 300;
								line-height: var(--px-24);
								color: var(--neutrals-900);
								width: 100%;
								.text-renderer {
									font-size: var(--px-14);
									font-weight: 300;
									line-height: var(--px-24);
									color: var(--neutrals-900);
								}
							}
						}
						&[col-id='stEntityName'] {
							.StyledStringColumn {
								font-size: var(--px-14);
								font-weight: 300;
								line-height: var(--px-24);
								color: var(--neutrals-900);
								width: 100%;
								.text-renderer {
									font-size: var(--px-14);
									font-weight: 300;
									line-height: var(--px-24);
									color: var(--neutrals-900);
								}
							}
						}
						&[col-id='actions'] {
							.StyledIconColumn {
								width: 100%;
								display: flex;
								justify-content: center;
								align-items: center;
								cursor: default;
								svg {
									cursor: pointer;
								}
								.edit-icon,
								.delete-icon {
									display: inline-flex;
									.motif-icon {
										display: inline-flex;
									}
								}
							}
						}
					}
				}
				.ag-cell {
					&[col-id='stEntityShortName'] {
						padding-left: var(--px-16);
						color: var(--neutrals-900);
					}
					&[col-id='stEntityName'] {
						color: var(--neutrals-900);
						font-size: var(--px-14);
						font-weight: 400;
					}
					&[col-id='entityGroupName'] {
						color: var(--neutrals-900);
					}
					&[col-id='actions'] {
						display: flex;
						align-items: center;
						justify-content: center;
						text-align: center;
						color: var(--neutrals-900);
						border-left: var(--px-2) solid var(--neutrals-200);
						padding-left: 0 !important;
					}
				}
			}
		}
	}
`;
